import * as ScrollArea from '@radix-ui/react-scroll-area';
import { styled } from '@stitches/react';
import MessageBubble from './MessageBubble';
import InputBar from './InputBar';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useChat } from '../context/ChatContext';
import { format, isSameDay, set } from 'date-fns';
import { es } from 'date-fns/locale';
import { each, filter, first, get, isUndefined, last, sortBy } from 'underscore';
import { useAuth } from '../context/AuthContext';

// #region Estilos
const WindowContainer = styled('div', {
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  backdropFilter: 'brightness(0.5)'
});

const Header = styled('div', {
  padding: '10px 16px',
  backgroundColor: '#1c2024',
  borderBottom: '1px solid #2d3135',
  display: 'flex',
  alignItems: 'center',
  height: '59px',
});

const ChatName = styled('div', {
  color: '#ffffff',
  fontSize: '16px',
  fontWeight: 500,
  width: '85%',
});

const ManagedBot = styled('div', {
  color: '#ffffff',
  fontSize: '16px',
  fontWeight: 500,
});

const MessagesContainer = styled(ScrollArea.Root, {
  flex: 1,
  padding: '20px',
  overflow: 'auto',
  paddingTop: '0',
  paddingBottom: '0',
});

const MessagesViewport = styled(ScrollArea.Viewport, {
  width: '100%',
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
});

const SendIcon = () => (
  <svg viewBox="0 0 24 24" width="24" height="24" fill="currentColor">
    <path d="M1.101 21.757L23.8 12.028 1.101 2.3l.011 7.912 13.623 1.816-13.623 1.817-.011 7.912z"></path>
  </svg>
);

const SwitchContainer = styled('div', {
  display: 'flex',
  alignItems: 'center',
  gap: '10px',
});

const SwitchLabel = styled('label', {
  display: 'flex',
  alignItems: 'center',
  cursor: 'pointer',
});

const SwitchInput = styled('input', {
  display: 'none',
  '&:checked + span': {
    backgroundColor: '#5B5BD6', // Cambia el fondo del slider cuando está activado
  },
  '&:checked + span::before': {
    transform: 'translateX(20px)', // Mueve el círculo hacia la derecha cuando está activado
  },
});

const SwitchSlider = styled('span', {
  width: '40px',
  height: '20px',
  backgroundColor: '#ccc',
  borderRadius: '20px',
  position: 'relative',
  transition: 'background-color 0.2s',
  '&::before': {
    content: '""',
    position: 'absolute',
    width: '16px',
    height: '16px',
    backgroundColor: '#fff',
    borderRadius: '50%',
    top: '2px',
    left: '2px',
    transition: 'transform 0.2s',
  },
});
//#endregion

function ChatWindow({ chat }) {
  const { socket } = useAuth();
  const { getMessages, setMessages, getHumanInControl, setHumanInControl } = useChat();
  const [renderedMessages, setRenderedMessages] = useState([]); // Estado para los mensajes renderizados
  const messages = useMemo(() => getMessages(chat?.id) || [], [chat?.id, getMessages]);
  const messagesViewportRef = useRef(null);
  const userIncomingName = first(filter(messages, (message) => message.direction === 'incoming'))?.userName ?? first(filter(messages, (message) => message.direction === 'incoming'))?.tags.name;
  const userIncomingPhone = first(filter(messages, (message) => message.direction === 'incoming'))?.userPhone ?? first(filter(messages, (message) => message.direction === 'incoming'))?.tags.userId;
  let lastHumanInControlState = last(filter(messages, (message) => !isUndefined(message.payload?.humanInControl)));

  // Actualiza los mensajes renderizados cuando cambia la conversación
  useEffect(() => {
    let humanState = isUndefined(lastHumanInControlState?.payload?.humanInControl) ? false : lastHumanInControlState?.payload?.humanInControl;
    setHumanInControl(humanState);

    if (chat?.id) {
      const updatedMessages = [];
      each(messages, (message, index) => {
        let currentMessage = message,
          previousMessage = messages[index - 1];

        // Verifica si la fecha es válida
        let currentDate = currentMessage.updatedAt ? new Date(currentMessage.updatedAt) : null,
          previousDate = previousMessage?.updatedAt ? new Date(previousMessage.updatedAt) : null;

        if (!currentDate || isNaN(currentDate)) {
          console.warn(`Fecha inválida en el mensaje con ID: ${currentMessage.id}`);
          return;
        }

        // Si es el primer mensaje o el día cambió, agrega un separador de fecha
        if (!previousDate || !isSameDay(currentDate, previousDate)) {
          updatedMessages.push(
            <div key={`date-${currentMessage.id}`} style={{ textAlign: 'center', margin: '10px 0', color: '#ffffff' }}>
              {format(currentDate, 'EEEE, dd MMMM yyyy', { locale: es })} {/* Formato de fecha en español */}
            </div>
          );
        }

        // Agrega el mensaje
        updatedMessages.push(
          <MessageBubble
            key={currentMessage.id} // Asegúrate de que cada mensaje tenga un key único
            message={currentMessage}
            isSending={currentMessage.isSending}
          />
        );
      });

      setRenderedMessages(updatedMessages); // Actualiza el estado con los mensajes renderizados
    }
  }, [chat?.id, messages]); // Se ejecuta cuando cambia la conversación o los mensajes

  // Hacer scroll al final cuando los mensajes cambien
  useEffect(() => {
    if (messagesViewportRef.current) {
      const viewport = messagesViewportRef.current;
      setTimeout(() => {
        viewport.scrollTop = viewport.scrollHeight;
      }, 50);
    }
  }, [renderedMessages]);

  // Manejo del envío de mensajes
  const handleSendMessage = async (messageText) => {
    if (!chat?.id) return;

    const newMessage = {
      id: `temp-${Date.now()}`,
      payload: {
        text: messageText,
        humanInControl: true
      },
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      isSending: true,
      direction: "outgoing",
      tags: {
        name: userIncomingName
      },
      conversationId: chat.id,
      type: "text"
    };

    setHumanInControl(true);
    setMessages(chat.id, [...messages, newMessage]);
    socket.emit('mensaje_desde_frontend', newMessage);
  };

  const handleAutoResponseToggle = (event) => {
    let state = !getHumanInControl();

    if (chat?.id) {
      const newMessage = {
        id: `temp-${Date.now()}`,
        payload: {
          text: '',
          humanInControl: state,
        },
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        isSending: true,
        direction: 'outgoing',
        tags: {},
        conversationId: chat.id,
        type: 'text',
        show: false,
      };

      setHumanInControl(state);
      setMessages(chat.id, [...messages, newMessage]);
      socket.emit('mensaje_desde_frontend', newMessage);
    }
  };

  return (
    <WindowContainer>
      <Header>
        <ChatName>{userIncomingName}<br />{userIncomingPhone}</ChatName>
        <ManagedBot style={{ display: 'flex' }}>
          Control Manual
          <SwitchContainer>
            <SwitchLabel style={{ marginLeft: '10px' }}>
              <SwitchInput
                type="checkbox"
                checked={getHumanInControl()}
                onChange={handleAutoResponseToggle}
              />
              <SwitchSlider />
            </SwitchLabel>
          </SwitchContainer>
        </ManagedBot>
      </Header>
      <MessagesContainer >
        <MessagesViewport ref={messagesViewportRef} style={{ overflow: 'auto' }}>
          {renderedMessages} {/* Renderiza los mensajes desde el estado */}
        </MessagesViewport>
      </MessagesContainer>
      <InputBar
        placeholder="Escribe un mensaje..."
        showInputButton={true}
        iconButton={<SendIcon />}
        handleSendMessage={handleSendMessage}
      />
    </WindowContainer>
  );
}

export default ChatWindow;